import { ImagePaths } from '../../lib/imageUrls.js';

export const detectingActiveDirectoryCompromise = {
  id: 55,
  slug: 'detecting-active-directory-compromise',
  ogImage: '/og/courses/detecting-active-directory-compromise.jpg',
  courseCode: 'AD-DET-001',
  featured: false,
  title: 'Detecting Active Directory Compromise',
  subtitle: 'Hunt for advanced identity attacks in on-premises and hybrid Active Directory environments',

  valueProposition: 'Most Australian ransomware incidents share a common signature: Active Directory compromise precedes encryption by days or weeks. This intensive workshop trains detection engineers and SOC analysts to identify Kerberos abuse, DCSync attacks, persistence techniques, and golden ticket scenarios using Windows event logs, Microsoft Defender for Identity, and Microsoft Sentinel — before attackers reach domain controller level.',

  duration: '16 hours (2 days intensive)',
  students: 'New',
  level: 'Advanced',
  image: ImagePaths.COURSE_2,
  shortDescription: 'AD-DET-001 — Active Directory threat detection workshop covering Kerberos abuse, DCSync, Pass-the-Hash, golden tickets, and MITRE ATT&CK identity techniques for Australian SOC teams.',

  price: {
    aud: 999,
    currency: 'AUD',
    earlyBird: 799
  },

  stripeConfig: {
    priceId: 'price_1U3WMqKu1nyc322UX6FAlJis',
    productId: 'prod_V3deIEUjecvyuR'
  },

  snapshot: {
    whoItsFor: [
      'SOC analysts and threat hunters responsible for Active Directory threat detection',
      'Incident response engineers investigating ransomware precursor activity',
      'Identity engineers who completed the Securing Active Directory course and want detection depth',
      'Security architects designing detection controls for ASD Essential Eight Maturity Level 2 compliance'
    ],
    accreditation: 'Aligned to MITRE ATT&CK for Enterprise — Credential Access, Lateral Movement, and Privilege Escalation tactics. Aligns to ACSC Essential Eight detect controls.'
  },

  logistics: {
    classSize: 'Maximum 10 participants — intensive lab-first format with attack-then-detect scenarios',
    requirements: [
      'Completion of Securing Active Directory (AD-SEC-001) or equivalent hands-on AD security experience',
      'Working knowledge of Windows event logging and basic KQL or Splunk query experience',
      'Microsoft Defender for Identity or Microsoft Sentinel access (trial acceptable for lab exercises)',
      'Lab environment with domain-joined Windows VMs provided — participants require laptop with browser access'
    ]
  },

  overview: 'This two-day intensive starts from the attacker\'s perspective. Each module begins with the attack technique — executed in the lab — and then works backwards through the artefacts it leaves in Windows Security event logs, Microsoft Defender for Identity alerts, and Sentinel analytics. Attack-then-detect sequencing is intentional: detection engineers who understand how an attack works write better detection rules than those who learn detection in isolation. The scenario set reflects real Australian incident patterns documented in ACSC Annual Cyber Threat Reports: Kerberoasting against service accounts, credential dumping from NTDS.dit, Pass-the-Hash lateral movement, and domain persistence via golden tickets and AdminSDHolder abuse. Each attack is mapped to MITRE ATT&CK and to the Essential Eight Maturity Level 2 detection requirements that frame IRAP assessment scope.',

  outcomes: [
    'Identify Kerberoasting and AS-REP Roasting attack artefacts in Windows Security event logs and Defender for Identity alerts',
    'Detect credential dumping techniques: LSASS memory access, NTDS.dit theft, and DCSync via Mimikatz',
    'Hunt for Pass-the-Hash and Pass-the-Ticket lateral movement using Sentinel KQL queries',
    'Detect domain persistence techniques: golden tickets, silver tickets, AdminSDHolder abuse, and skeleton keys',
    'Write Microsoft Sentinel analytics rules and hunting queries for Active Directory attack patterns',
    'Triage Defender for Identity alerts and correlate with Windows event logs for incident investigation',
    'Map detections to MITRE ATT&CK techniques and to ASD Essential Eight Maturity Level 2 detect controls',
    'Build an AD compromise detection runbook ready for SOC playbook integration'
  ],

  audience: [
    'SOC analysts and threat hunters responsible for Active Directory detection coverage',
    'Detection engineers writing Sentinel analytics rules for identity attack patterns',
    'Incident responders investigating ransomware precursor activity in Australian organisations',
    'Identity engineers who completed Securing Active Directory and want detection depth',
    'Security architects evidencing Essential Eight Maturity Level 2 detect controls for IRAP scope',
    'MSSP analysts monitoring hybrid Active Directory estates across multiple client tenants'
  ],

  prerequisites: [
    'Completion of Securing Active Directory (AD-SEC-001) or equivalent hands-on AD security experience',
    'Working knowledge of Windows Security event logging and event ID interpretation',
    'Basic KQL or Splunk query experience — advanced query construction is taught in the course',
    'Microsoft Defender for Identity or Microsoft Sentinel access (trial acceptable)',
    'A laptop with a modern browser — the domain-joined lab environment is provided'
  ],

  modules: [
    {
      number: 1,
      title: 'Reconnaissance and Initial Access Detection',
      duration: '4 hours',
      topics: [
        'Active Directory enumeration: BloodHound, SharpHound, and LDAP query detection in Security event logs',
        'Kerberoasting: SPN enumeration, TGS-REQ patterns, and detection via Event ID 4769 and Defender for Identity',
        'AS-REP Roasting: detecting user accounts with Kerberos pre-authentication disabled',
        'Password spray and credential stuffing: detecting distributed authentication attempts across DCs',
        'ACSC threat context: how initial access translates to AD enumeration in Australian ransomware incidents'
      ]
    },
    {
      number: 2,
      title: 'Credential Theft and Privilege Escalation Detection',
      duration: '4 hours',
      topics: [
        'LSASS credential dumping: detecting process access events (Event ID 4656, 10) and Defender for Endpoint alerts',
        'DCSync detection: replication directory service changes (Event ID 4662) and Defender for Identity DCSync alert',
        'NTDS.dit theft: VSS shadow copy abuse, ntdsutil.exe usage, and backup operator privilege abuse',
        'Mimikatz detection: process signatures, command-line patterns, and AMSI bypass techniques',
        'Pass-the-Hash lateral movement: Event IDs 4624 (Logon Type 3) and 4648 tracking across DCs'
      ]
    },
    {
      number: 3,
      title: 'Lateral Movement and Domain Persistence Detection',
      duration: '4 hours',
      topics: [
        'Pass-the-Ticket and Overpass-the-Hash: Kerberos ticket anomalies and encryption downgrade alerts',
        'Golden ticket detection: Defender for Identity golden ticket alert and Event ID 4769 anomaly patterns',
        'Domain persistence: AdminSDHolder abuse, DCSync rights delegation, and Group Policy Object modification',
        'Skeleton key malware: detecting LSASS patching and authentication bypass techniques',
        'Microsoft Sentinel KQL hunting: building cross-DC correlation queries for lateral movement chains'
      ]
    },
    {
      number: 4,
      title: 'Detection Engineering and Incident Response Capstone',
      duration: '4 hours',
      topics: [
        'Write Microsoft Sentinel analytics rules for the attack patterns from Modules 1-3',
        'Build a Defender for Identity custom detection tuning plan: reducing false positives while maintaining coverage',
        'Incident response tabletop: complete AD compromise kill chain from initial access to golden ticket',
        'ASD Essential Eight Maturity Level 2 detection evidence: mapping alerts to ISM control families',
        'Detection runbook deliverable: AD compromise detection playbook for SOC integration'
      ]
    }
  ],

  format: {
    type: 'Live online attack-then-detect workshop — 2 days intensive with a provided lab forest',
    includes: [
      '16 hours of lab-first instruction capped at 10 participants',
      'Pre-built lab forest with domain-joined Windows Server and Windows 11 VMs',
      'Mimikatz and BloodHound attack exercises paired with the detection artefacts they leave behind',
      'Microsoft Defender for Identity deployment and a Sentinel workspace pre-loaded with AD connector data',
      'Sentinel analytics rules and hunting queries for every attack technique covered',
      'Defender for Identity alert tuning plan for reducing false positives without losing coverage',
      'AD compromise detection runbook deliverable ready for SOC playbook integration',
      'Recorded sessions for 6 months and a certificate of completion'
    ]
  },

  nextIntake: {
    date: 'Register your interest — we will confirm the next cohort',
    details: 'Two-day intensive cohorts run every six weeks, commonly booked back-to-back with Securing Active Directory. Private cohorts can model your own forest topology in the lab'
  },

  certification: 'No vendor exam is associated with this workshop. Participants receive an EDUC4TE certificate of completion. This workshop prepares detection engineers for the practical AD detection content in the SC-200 and SC-100 Microsoft certification exams.',

  faqs: [
    {
      question: 'Is this course only for organisations with on-premises Active Directory?',
      answer: 'No. The techniques taught are relevant to both on-premises Active Directory and hybrid environments where AD Connect Sync is used alongside Microsoft Entra ID. Defender for Identity protects both on-prem DCs and hybrid identity flows, so the detection content applies directly to most Australian enterprise environments.'
    },
    {
      question: 'How does this course complement the Securing Active Directory course?',
      answer: 'Securing Active Directory (AD-SEC-001) focuses on hardening controls — tier model, LAPS, DC patching, and forest recovery. This course focuses on detection — identifying when those hardening controls have been bypassed or when an attacker is working towards bypassing them. They are designed as a pair: harden first, then build detections.'
    },
    {
      question: 'Do participants need to bring their own lab environment?',
      answer: 'No. We provide a pre-built lab environment with domain-joined Windows Server and Windows 11 VMs, Mimikatz and BloodHound installed for attack exercises, Microsoft Defender for Identity deployed, and a Sentinel workspace pre-configured with AD connector data. All participants need is a laptop with a modern browser.'
    },
    {
      question: 'Is the content aligned to IRAP assessment requirements?',
      answer: 'Yes. Module 4 explicitly maps detections to ACSC ISM identity and access management controls used in IRAP assessments, and aligns to Essential Eight Maturity Level 2 detection requirements for Restrict Administrative Privileges and Patch Operating Systems. Detection evidence produced in the capstone can form part of an IRAP evidence pack.'
    }
  ],

  australianContext: true,
  complianceFrameworks: ['ASD Essential Eight', 'ACSC ISM', 'ACSC Threat Intelligence', 'MITRE ATT&CK']
};
